// "Nicht übernommen": every topic of a meeting that became no
// issue, with its reason. Owner only: the link page never renders it, and its
// DTO cannot carry it (`MeetingLinkReviewDto` is an allowlist).
//
// - The chip names the reason. A status update about `#N` says what happened
//   to it: "Statusbericht · Kommentar auf #42" once the comment is posted,
//   "Statusbericht · betrifft #42" otherwise; when the same meeting item
//   also became a new issue, the row says so ("Neu dazu: #57", or "noch
//   nicht angelegt"), and that issue's row says "Bezug: #42".
//   A held item says "Rückfrage
//   offen" until the doubt card has its answer: then "Nicht angelegt" (a
//   skip), the reason Loom held it with (kept as no issue), or "Beantwortet".
// - The summary and quotes live 30 days; after that the row is only its chip
//   and "bei Minute mm:ss".
// - "Doch übernehmen" files the topic as a ticket or an epic while its
//   content still exists, and never twice (a promoted topic says so).

import { useState } from "react";

import { TOPIC_REASONS, meetingClock, type NotTakenRowDto, type TicketClass } from "@loom/shared/meetings";

import { useT, type MessageKey } from "../../i18n/index.js";

import { QuoteList } from "./MeetingReview";

export const notTakenChipOf = (
  topic: Pick<NotTakenRowDto, "reason" | "statusComment" | "held">,
): { readonly key: MessageKey; readonly iid?: number } => {
  const held = topic.held ?? null;
  if (topic.reason === "doubt" && held !== null && held.answer !== null) {
    if (held.answer === "skip") return { key: "meetings.notTaken.reason.doubtSkipped" };
    const kept = held.reason;
    if (
      held.answer === "as_held" &&
      held.class === "none" &&
      kept !== null &&
      kept !== "doubt" &&
      (TOPIC_REASONS as readonly string[]).includes(kept)
    ) {
      return { key: `meetings.notTaken.reason.${kept}` as MessageKey };
    }
    return { key: "meetings.notTaken.reason.doubtAnswered" };
  }
  if (topic.reason === "status_update" && topic.statusComment !== null) {
    return {
      key:
        topic.statusComment.state === "posted"
          ? "meetings.notTaken.comment"
          : "meetings.notTaken.concerns",
      iid: topic.statusComment.iid,
    };
  }
  return { key: `meetings.notTaken.reason.${topic.reason}` as MessageKey };
};

export function NotTakenList({
  topics,
  busyTopicId,
  onPromote,
}: {
  readonly topics: readonly NotTakenRowDto[];
  /** The topic a promote is running for; its buttons are inert meanwhile. */
  readonly busyTopicId: string | null;
  /** Absent: no "Doch übernehmen" (the meeting has no project yet). */
  readonly onPromote?: (topicId: string, as: TicketClass) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState<string | null>(null);
  if (topics.length === 0) return null;
  return (
    <section className="meet-section" aria-label={t("meetings.notTaken.title")}>
      <h2 className="meet-section-title">{t("meetings.notTaken.title")}</h2>
      <ul className="meet-list">
        {[...topics]
          .sort((a, b) => a.startMs - b.startMs)
          .map((topic) => {
            const chip = notTakenChipOf(topic);
            const expired = topic.summary === null && topic.quotes === null;
            const canPromote =
              onPromote !== undefined && !expired && topic.promotedTicketId === null;
            // A status row whose item also became a new issue: "Neu dazu: #M".
            const related = topic.relatedIssue ?? null;
            return (
              <li key={topic.topicId} className="meet-row" data-topic={topic.topicId}>
                <div className="meet-row-head">
                  <span className="focus-chip">
                    {t(chip.key, chip.iid === undefined ? undefined : { iid: chip.iid })}
                  </span>
                  <span className="meet-meta">
                    {t("meetings.quote.at", { clock: meetingClock(topic.startMs) })}
                  </span>
                </div>
                {topic.summary !== null && <p className="meet-summary">{topic.summary}</p>}
                {topic.quotes !== null && topic.quotes.length > 0 && (
                  <QuoteList quotes={topic.quotes} />
                )}
                {topic.promotedTicketId !== null && (
                  <span className="meet-meta">{t("meetings.notTaken.promoted")}</span>
                )}
                {related !== null && related.ticketId !== topic.promotedTicketId && (
                  <span className="meet-meta">
                    {related.iid === null
                      ? t("meetings.notTaken.relatedPending")
                      : t("meetings.notTaken.relatedFiled", { iid: related.iid })}
                  </span>
                )}
                {canPromote &&
                  (open === topic.topicId ? (
                    <div className="meet-actions">
                      {(["ticket", "epic"] as const).map((as) => (
                        <button
                          key={as}
                          type="button"
                          className="focus-btn is-ghost"
                          disabled={busyTopicId !== null}
                          onClick={() => onPromote(topic.topicId, as)}
                        >
                          {t(as === "ticket" ? "meetings.notTaken.asTicket" : "meetings.notTaken.asEpic")}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="focus-deeplink"
                        disabled={busyTopicId !== null}
                        onClick={() => setOpen(null)}
                      >
                        {t("common.cancel")}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="focus-deeplink"
                      disabled={busyTopicId !== null}
                      onClick={() => setOpen(topic.topicId)}
                    >
                      {t("meetings.notTaken.promote")}
                    </button>
                  ))}
              </li>
            );
          })}
      </ul>
    </section>
  );
}
